<template>
    <div class="flex mt-[42px] mb-[27px] justify-between h-[42px]">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.billing") }}
        </h1>
    </div>

    <BillingTabs class="mt-[24px]" tab-key="subscriptions">
        <!-- God Mode Enterprise Plan Section -->
        <div v-if="hasGodMode" class="mb-[98px]">
            <div class="mt-[42px] grid-cols-layout pb-5 lg:grid">
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="mb-[5px] text-[15.75px] font-medium leading-[142%] tracking-[-0.01em] text-[#161E2E]"
                    >
                        {{ $t("vue.your_current_plan") }}
                    </h3>
                    <p
                        class="max-w-[333px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                    >
                        {{ $t("vue.your_current_dmarc_subscription") }}
                    </p>
                </div>

                <!-- ENTERPRISE PLAN FOR GOD MODE -->
                <div
                    class="mx-auto w-full bg-white lg:mx-0 rounded-[12.8px] border border-[#E5E7EB]"
                >
                    <div
                        class="grid w-full grid-cols-12 items-start gap-[24px] p-[21px]"
                        :class="{
                            'border-b border-[#E5E7EB]':
                                currentPaddleSubscription,
                        }"
                    >
                        <!-- LEFT -->
                        <div class="col-span-8 max-w-[434px] pr-[19px]">
                            <h2
                                class="text-[14px] font-medium leading-[142%] text-[#000000]"
                            >
                                Enterprise {{ $t("vue.plan") }}
                            </h2>
                            <p
                                class="mt-[2px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                            >
                                {{
                                    $t(
                                        "vue.billing_enterprise_unlimited_access",
                                    )
                                }}
                            </p>

                            <div class="mt-[21px] space-y-[12px]">
                                <div class="grid grid-cols-12">
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{ $t("vue.data_history") }}
                                    </div>
                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                    >
                                        {{ $t("vue.unlimited") }}
                                    </div>
                                </div>

                                <div class="grid grid-cols-12">
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{ $t("vue.features") }}
                                    </div>

                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                    >
                                        {{ $t("vue.all_features_enabled") }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- RIGHT BOX -->
                        <div class="col-span-4 justify-self-end">
                            <div
                                class="flex flex-col items-center justify-center rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] w-[260px] h-[120px]"
                            >
                                <div
                                    class="text-[24px] font-semibold leading-none text-[#111827]"
                                >
                                    ✓ {{ $t("vue.active") }}
                                </div>
                                <!--                                <div class="mt-[8px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]">Status</div>-->
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-end h-[49px]">
                        <div class="pr-6">
                            <AtomicButton
                                v-if="currentPaddleSubscription"
                                class="mt-2"
                                color="light-blue"
                                @click="visitCustomerPortal()"
                            >
                                {{ $t("vue.manage_subscription_invoices") }}
                            </AtomicButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main content -->
        <div v-if="!hasGodMode" class="mb-[98px]">
            <div
                class="mt-[42px] grid-cols-layout pb-5 lg:grid"
                @submit.prevent="updateNameAndEmail"
            >
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="mb-[5px] text-[15.75px] font-medium leading-[142%] tracking-[-0.01em] text-[#161E2E]"
                    >
                        {{ $t("vue.your_current_plan") }}
                    </h3>
                    <p
                        class="max-w-[333px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                    >
                        {{ $t("vue.your_current_dmarc_subscription") }}
                    </p>
                </div>

                <!-- Loading skeleton for Your Current Plan -->
                <div
                    v-if="isLoading"
                    class="mx-auto min-h-[265px] w-full bg-white rounded-[12.8px]"
                >
                    <div class="grid h-full w-full grid-cols-12 p-[19px]">
                        <div class="col-span-7 px-6 py-4">
                            <div
                                class="h-8 w-48 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div
                                class="mt-2 h-4 w-64 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div class="mt-6 space-y-2">
                                <div
                                    class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                            </div>
                        </div>
                        <div
                            class="col-span-5 flex flex-col items-center justify-center rounded-[12.8px] border bg-gray-100"
                        >
                            <div
                                class="h-12 w-16 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div
                                class="mt-2 h-4 w-20 animate-pulse rounded bg-gray-200"
                            ></div>
                        </div>
                    </div>
                </div>

                <!-- TRIAL STATE -->
                <div
                    v-if="isTrialing && !isSubscribed && !isLoading"
                    class="mx-auto w-full bg-white lg:mx-0 rounded-[12.8px] border border-[#E5E7EB]"
                >
                    <div
                        class="grid w-full grid-cols-12 items-start gap-[24px] p-[21px]"
                        :class="{
                            'border-b border-[#E5E7EB]':
                                currentPaddleSubscription,
                        }"
                    >
                        <!-- LEFT -->
                        <div class="col-span-8 max-w-[434px] pr-[19px]">
                            <h2
                                class="text-[14px] font-medium leading-[142%] text-[#000000]"
                            >
                                {{ $t("vue.free_trial") }}
                            </h2>
                            <p
                                class="mt-[2px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                            >
                                {{ $t("vue.explore_all_features_free") }}
                            </p>

                            <div class="mt-[21px] space-y-[12px]">
                                <div class="grid grid-cols-12">
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{ $t("vue.trial_status") }}
                                    </div>
                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                    >
                                        {{
                                            isTrialing
                                                ? $t("vue.active")
                                                : $t("vue.expired")
                                        }}
                                    </div>
                                </div>

                                <div class="grid grid-cols-12">
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{ $t("vue.trial_expires") }}
                                    </div>
                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                    >
                                        {{ trialEndsAt }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- RIGHT BOX -->
                        <div class="col-span-4 justify-self-end">
                            <div
                                class="flex flex-col items-center justify-center rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] w-[260px] h-[120px]"
                            >
                                <div
                                    class="text-[24px] font-semibold leading-none text-[#111827]"
                                >
                                    {{ domainsCount }}
                                </div>
                                <div
                                    class="mt-[8px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                                >
                                    {{ $t("vue.your_domains") }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- FOOTER ACTION -->
                    <div
                        v-if="currentPaddleSubscription"
                        class="flex justify-end h-[49px] border-t border-[#E5E7EB]"
                    >
                        <div class="pr-6">
                            <AtomicButton
                                class="mt-2"
                                color="light-blue"
                                @click="visitCustomerPortal()"
                            >
                                {{ $t("vue.manage_subscription_invoices") }}
                            </AtomicButton>
                        </div>
                    </div>
                </div>

                <!-- TRIAL ENDED STATE -->
                <div
                    v-if="!isTrialing && !isSubscribed && !isLoading"
                    class="mx-auto w-full bg-white lg:mx-0 rounded-[12.8px] border border-[#E5E7EB]"
                >
                    <div
                        class="grid w-full grid-cols-12 items-start gap-[24px] p-[21px]"
                    >
                        <!-- LEFT -->
                        <div class="col-span-8">
                            <h2
                                class="text-[14px] font-medium leading-[142%] text-[#000000]"
                            >
                                {{ $t("vue.free_trial") }}
                            </h2>
                            <p
                                class="mt-[2px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                            >
                                {{ $t("vue.explore_all_features_free") }}
                            </p>

                            <div class="mt-[21px] space-y-[12px]">
                                <div class="grid grid-cols-12">
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{ $t("vue.trial_status") }}
                                    </div>
                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                    >
                                        {{
                                            isTrialing
                                                ? $t("vue.active")
                                                : $t("vue.expired")
                                        }}
                                    </div>
                                </div>

                                <div
                                    v-if="trialEndsAt"
                                    class="grid grid-cols-12"
                                >
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{ $t("vue.trial_expires") }}
                                    </div>
                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                    >
                                        {{ trialEndsAt }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- RIGHT BOX -->
                        <div class="col-span-4 justify-self-end">
                            <div
                                class="flex flex-col items-center justify-center rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] w-[260px] h-[120px]"
                            >
                                <div
                                    class="text-[24px] font-semibold leading-none text-[#111827]"
                                >
                                    {{ domainsCount }}
                                </div>
                                <div
                                    class="mt-[8px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                                >
                                    {{ $t("vue.your_domains") }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- FOOTER ACTION -->
                    <div
                        v-if="currentPaddleSubscription"
                        class="flex justify-end h-[49px] border-t border-[#E5E7EB]"
                    >
                        <div class="pr-6">
                            <AtomicButton
                                class="mt-2"
                                color="light-blue"
                                @click="visitCustomerPortal()"
                            >
                                {{ $t("vue.manage_subscription_invoices") }}
                            </AtomicButton>
                        </div>
                    </div>
                </div>

                <!-- SUBSCRIBED STATE -->
                <div
                    v-if="isSubscribed && !isLoading"
                    class="mx-auto w-full bg-white lg:mx-0 rounded-[12.8px] border border-[#E5E7EB]"
                >
                    <div
                        class="grid w-full grid-cols-12 items-start gap-[24px] p-[21px] border-b border-[#E5E7EB]"
                    >
                        <!-- LEFT -->
                        <div class="col-span-8 max-w-[434px] pr-[19px]">
                            <h2
                                class="text-[14px] font-medium leading-[142%] text-[#000000]"
                            >
                                {{
                                    currentPaddleSubscription?.items[0]?.productName
                                        ?.replace(/DMARC\/|eye\s*-\s*/g, "")
                                        .trim() || "Unknown Plan"
                                }}
                            </h2>
                            <p
                                class="mt-[2px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                            >
                                {{
                                    currentPaddleSubscription?.items[0]
                                        ?.productDescription ||
                                    "No description available"
                                }}
                            </p>

                            <div class="mt-[21px] space-y-[12px]">
                                <div class="grid grid-cols-12">
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{ $t("vue.payment_cycle") }}
                                    </div>
                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%]"
                                        :class="
                                            !currentPaddleSubscription?.next_billing_at
                                                ? 'text-red-500'
                                                : 'text-gray-500'
                                        "
                                    >
                                        {{
                                            !currentPaddleSubscription?.next_billing_at
                                                ? $t(
                                                      "vue.scheduled_to_be_canceled",
                                                  )
                                                : currentPaddleSubscription?.period ===
                                                    "year"
                                                  ? $t("vue.yearly")
                                                  : $t("vue.monthly")
                                        }}
                                        <span
                                            v-if="
                                                currentPaddleSubscription?.next_billing_at
                                            "
                                            class="cursor-pointer font-semibold text-orange-500"
                                            @click="updateFrequency()"
                                        >
                                            {{
                                                currentPaddleSubscription?.period ===
                                                "year"
                                                    ? $t(
                                                          "vue.switch_to_monthly",
                                                      )
                                                    : $t("vue.switch_to_yearly")
                                            }}
                                        </span>
                                    </div>
                                </div>

                                <div class="grid grid-cols-12">
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{
                                            currentPaddleSubscription.change
                                                .action === "cancel"
                                                ? $t(
                                                      "vue.scheduled_to_be_canceled_at",
                                                  )
                                                : $t("vue.next_invoice")
                                        }}
                                    </div>
                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%]"
                                        :class="
                                            currentPaddleSubscription?.next_billing_at
                                                ? 'text-gray-500'
                                                : 'text-red-500'
                                        "
                                    >
                                        {{
                                            currentPaddleSubscription?.next_billing_at
                                                ? new Date(
                                                      currentPaddleSubscription
                                                          ?.next_billing_at
                                                          .date,
                                                  ).toLocaleDateString(
                                                      "en-US",
                                                      {
                                                          day: "numeric",
                                                          month: "long",
                                                          year: "numeric",
                                                      },
                                                  )
                                                : currentPaddleSubscription
                                                        ?.currentBillingPeriod
                                                        ?.start
                                                  ? new Date(
                                                        currentPaddleSubscription
                                                            ?.currentBillingPeriod
                                                            .start.date,
                                                    ).toLocaleDateString(
                                                        "en-US",
                                                        {
                                                            day: "numeric",
                                                            month: "long",
                                                            year: "numeric",
                                                        },
                                                    )
                                                  : "N/A"
                                        }}
                                    </div>
                                </div>

                                <div
                                    v-if="
                                        currentPaddleSubscription.change
                                            .action !== 'cancel'
                                    "
                                    class="grid grid-cols-12"
                                >
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{ $t("vue.next_payment") }}
                                    </div>
                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                    >
                                        ${{
                                            currentPaddleSubscription?.totals
                                                ?.total
                                        }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- RIGHT BOX -->
                        <div class="col-span-4 justify-self-end">
                            <div
                                class="flex flex-col items-center justify-center rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] w-[260px] h-[120px]"
                            >
                                <div
                                    class="text-[24px] font-semibold leading-none text-[#111827]"
                                >
                                    {{ domainsCount }}
                                </div>
                                <div
                                    class="mt-[8px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                                >
                                    {{ $t("vue.your_domains") }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- FOOTER ACTION -->
                    <div class="flex justify-end h-[49px]">
                        <div class="pr-6">
                            <AtomicButton
                                class="mt-2"
                                color="light-blue"
                                @click="visitCustomerPortal()"
                            >
                                {{ $t("vue.manage_subscription_invoices") }}
                            </AtomicButton>
                        </div>
                    </div>
                </div>
            </div>

            <!--PLANS OVERVIEW-->
            <div class="mt-5 grid-cols-layout pb-5 lg:grid">
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="mb-[5px] text-[15.75px] font-medium leading-[142%] tracking-[-0.01em] text-[#161E2E]"
                    >
                        {{ $t("vue.available_dmarc_subscription") }}
                    </h3>
                    <p
                        class="max-w-[333px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                    >
                        {{ $t("vue.check_pricing_page_info") }}
                    </p>
                </div>

                <div class="flex grow flex-col gap-6">
                    <!-- Loading state for plans -->
                    <div
                        v-if="isLoading"
                        class="mx-auto min-h-[265px] w-full bg-white rounded-[12.8px]"
                    >
                        <div class="grid h-full w-full grid-cols-12 p-[19px]">
                            <div class="col-span-7 px-6 py-4">
                                <div
                                    class="h-8 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="mt-2 h-4 w-64 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div class="mt-6 space-y-2">
                                    <div
                                        class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                    ></div>
                                    <div
                                        class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                    ></div>
                                    <div
                                        class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                    ></div>
                                </div>
                            </div>
                            <div
                                class="col-span-5 flex flex-col items-center justify-center rounded-[12.8px] border bg-gray-100"
                            >
                                <div
                                    class="h-8 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="mt-4 h-12 w-16 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="mt-2 h-4 w-20 animate-pulse rounded bg-gray-200"
                                ></div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-for="(plan, key) in groupedPlansByProduct"
                        :key="key"
                        class="mx-auto w-full bg-white lg:mx-0 rounded-[12.8px] border border-[#E5E7EB]"
                    >
                        <div
                            class="grid w-full grid-cols-12 items-start gap-[24px] p-[21px] border-b border-[#E5E7EB]"
                        >
                            <!-- LEFT -->
                            <div class="col-span-8 max-w-[434px] pr-[19px]">
                                <h2
                                    class="text-[14px] font-medium leading-[142%] text-[#000000]"
                                >
                                    {{
                                        plan[0].productName
                                            .replace(/DMARC\/|eye\s*-\s*/g, "")
                                            .trim()
                                    }}
                                </h2>
                                <p
                                    class="mt-[2px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                                >
                                    {{ plan[0].productDescription }}
                                </p>

                                <div class="mt-[21px] space-y-[12px]">
                                    <div class="grid grid-cols-12">
                                        <div
                                            class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                        >
                                            {{ $t("vue.payment") }}
                                        </div>
                                        <div
                                            class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                        >
                                            ${{
                                                payYearly
                                                    ? plan[1].totalPrice
                                                    : plan[0].totalPrice
                                            }}/{{
                                                payYearly
                                                    ? t("vue.year")
                                                    : t("vue.month")
                                            }}
                                            {{ $t("vue.per") }}
                                            {{
                                                plan[0].productName
                                                    .replace(
                                                        /DMARC\/|eye\s*-\s*/g,
                                                        "",
                                                    )
                                                    .trim() === "Basic"
                                                    ? t("vue.plan")
                                                    : t("vue.domain")
                                            }}
                                        </div>
                                    </div>

                                    <div class="grid grid-cols-12">
                                        <div
                                            class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                        >
                                            {{
                                                plan[0].productName
                                                    .replace(
                                                        /DMARC\/|eye\s*-\s*/g,
                                                        "",
                                                    )
                                                    .trim() === "Basic"
                                                    ? t("vue.maximum_domains")
                                                    : t("vue.domains")
                                            }}
                                        </div>
                                        <div
                                            class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                        >
                                            {{
                                                plan[0].productName
                                                    .replace(
                                                        /DMARC\/|eye\s*-\s*/g,
                                                        "",
                                                    )
                                                    .trim() === "Basic"
                                                    ? "1"
                                                    : "2 - 50"
                                            }}
                                        </div>
                                    </div>

                                    <div class="grid grid-cols-12">
                                        <div
                                            class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                        >
                                            {{ $t("vue.data_history") }}
                                        </div>
                                        <div
                                            class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                        >
                                            {{
                                                plan[0].productName
                                                    .replace(
                                                        /DMARC\/|eye\s*-\s*/g,
                                                        "",
                                                    )
                                                    .trim() === "Basic"
                                                    ? `30 ${$t("vue.days")}`
                                                    : `1 ${$t("vue.year")}`
                                            }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- RIGHT BOX -->
                            <div class="col-span-4 justify-self-end">
                                <div
                                    class="flex flex-col items-center justify-center rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] w-[260px] h-[120px]"
                                >
                                    <div
                                        class="flex flex-col items-center justify-center"
                                    >
                                        <div
                                            class="text-[24px] font-semibold leading-none text-[#111827]"
                                        >
                                            ${{
                                                payYearly
                                                    ? plan[1].totalPrice / 12
                                                    : plan[0].totalPrice
                                            }}
                                        </div>
                                        <div
                                            class="mt-[8px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                                        >
                                            {{
                                                plan[0].productName
                                                    .replace(
                                                        /DMARC\/|eye\s*-\s*/g,
                                                        "",
                                                    )
                                                    .trim() !== "Basic"
                                                    ? t("vue.monthly")
                                                    : t("vue.monthly")
                                            }}
                                        </div>
                                    </div>

                                    <div
                                        class="mt-3 mb-2 flex items-center gap-3"
                                    >
                                        <Switch
                                            v-model="payYearly"
                                            :left-label="$t('vue.pay_monthly')"
                                            :right-label="$t('vue.pay_yearly')"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- FOOTER ACTION -->
                        <div class="flex justify-end h-[49px]">
                            <div class="pr-6 group relative">
                                <AtomicButton
                                    v-if="isSubscribed"
                                    class="mt-2"
                                    :disabled="
                                        isBasicPlanDisabled &&
                                        plan[0].productName
                                            .replace(/DMARC\/|eye\s*-\s*/g, '')
                                            .trim() === 'Basic'
                                    "
                                    @click="switchToPlan(plan)"
                                >
                                    {{ $t("vue.switch_to_this_plan") }}
                                </AtomicButton>

                                <AtomicButton
                                    v-else
                                    class="mt-2"
                                    :disabled="
                                        isBasicPlanDisabled &&
                                        plan[0].productName
                                            .replace(/DMARC\/|eye\s*-\s*/g, '')
                                            .trim() === 'Basic'
                                    "
                                    @click="handlePlanSelection(plan)"
                                >
                                    {{ $t("vue.change_plan") }}
                                </AtomicButton>

                                <div
                                    v-if="
                                        isBasicPlanDisabled &&
                                        plan[0].productName
                                            .replace(/DMARC\/|eye\s*-\s*/g, '')
                                            .trim() === 'Basic'
                                    "
                                    class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 transform opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                                >
                                    <div
                                        class="whitespace-nowrap rounded bg-gray-800 px-3 py-2 text-xs text-white"
                                    >
                                        {{
                                            $t("vue.basic_plan_limited_message")
                                        }}
                                        {{ $t("vue.you_currently_have") }}
                                        {{ domainsCount }} domains.
                                        <div class="tooltip-triangle"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ENTERPRISE LEVEL PLAN OPTION-->
            <div class="mt-5 grid-cols-layout pb-5 lg:grid">
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="mb-[5px] text-[15.75px] font-medium leading-[142%] tracking-[-0.01em] text-[#161E2E]"
                    >
                        {{ $t("vue.dmarceye_for_big_teams") }}
                    </h3>
                    <p
                        class="max-w-[333px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                    >
                        {{ $t("vue.reach_out_to_our_sales_tailored_offer") }}
                    </p>
                </div>

                <!-- Loading skeleton for DMARCeye for big teams -->
                <div
                    v-if="isLoading"
                    class="mx-auto min-h-[265px] w-full bg-white rounded-[12.8px]"
                >
                    <div class="grid h-full w-full grid-cols-12 p-[19px]">
                        <div class="col-span-7 px-6 py-4">
                            <div
                                class="h-8 w-32 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div
                                class="mt-2 h-4 w-64 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div class="mt-6 space-y-2">
                                <div
                                    class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                                <div
                                    class="h-4 w-32 animate-pulse rounded bg-gray-200"
                                ></div>
                            </div>
                        </div>
                        <div
                            class="col-span-5 flex flex-col items-center justify-center rounded-[12.8px] border bg-gray-100"
                        >
                            <div
                                class="h-8 w-32 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div
                                class="mt-4 h-12 w-16 animate-pulse rounded bg-gray-200"
                            ></div>
                            <div
                                class="mt-2 h-4 w-20 animate-pulse rounded bg-gray-200"
                            ></div>
                        </div>
                    </div>
                </div>

                <!-- Enterprise -->
                <div
                    v-if="!isLoading"
                    class="mx-auto w-full bg-white lg:mx-0 rounded-[12.8px] border border-[#E5E7EB]"
                >
                    <div
                        class="grid w-full grid-cols-12 items-start gap-[24px] p-[21px] border-b border-[#E5E7EB]"
                    >
                        <!-- LEFT -->
                        <div class="col-span-8 max-w-[434px] pr-[19px]">
                            <h2
                                class="text-[14px] font-medium leading-[142%] text-[#000000]"
                            >
                                Enterprise
                            </h2>
                            <p
                                class="mt-[2px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                            >
                                For 50+ domains. Includes extended data
                                retention and enhanced support.
                            </p>

                            <div class="mt-[21px] space-y-[12px]">
                                <div class="grid grid-cols-12">
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{ $t("vue.payment") }}
                                    </div>
                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                    >
                                        {{ $t("vue.yearly_commitment_only") }}
                                    </div>
                                </div>

                                <div class="grid grid-cols-12">
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{ $t("vue.domains") }}
                                    </div>
                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                    >
                                        50+
                                    </div>
                                </div>

                                <div class="grid grid-cols-12">
                                    <div
                                        class="col-span-4 text-[12.25px] font-medium leading-[142%] text-[#000000]"
                                    >
                                        {{ $t("vue.data_history") }}
                                    </div>
                                    <div
                                        class="col-span-8 text-[12.25px] font-normal leading-[142%] text-[#6B7280]"
                                    >
                                        {{ $t("vue.unlimited") }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- RIGHT BOX -->
                        <div class="col-span-4 justify-self-end">
                            <div
                                class="flex flex-col items-center justify-center rounded-[12.8px] border border-[#E5E7EB] bg-[#F3F4F6] w-[260px] h-[120px]"
                            >
                                <div
                                    class="text-[24px] font-semibold leading-none text-[#111827]"
                                >
                                    {{ $t("vue.from") }} $300
                                </div>
                                <div
                                    class="mt-[8px] text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                                >
                                    {{ $t("vue.monthly") }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-end h-[49px]">
                        <div class="pr-6">
                            <a
                                href="https://zapti.me/dmarceye/sales"
                                target="_blank"
                            >
                                <AtomicButton class="mt-2" color="light-blue">
                                    {{ $t("vue.contact_sales") }}
                                </AtomicButton>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <SubscriptionBuyModal
            v-if="selectedPlan"
            :paddle-client-token="props.paddleClientToken"
            :selected-plan="selectedPlan"
            :paddle-environment="props.paddleEnvironment"
            :domains-count="domainsCountToBuy"
            :period="payYearly ? t('vue.yearly') : t('vue.monthly')"
            @close="handleCloseSubscriptionModal"
        />

        <SubscriptionSwitchFrequencyModal
            v-if="showFrequencySwitchModal"
            :current-frequency="currentFrequency"
            :current-price="currentPaddleSubscription?.totals?.total || '0'"
            :current-plan-name="
                currentPaddleSubscription?.items?.[0]?.productName
                    ?.replace(/DMARC\/|eye\s*-\s*/g, '')
                    .trim() || t('vue.unknown_plan')
            "
            :next-billing-date="
                currentPaddleSubscription?.next_billing_at?.date || ''
            "
            @close="handleFrequencyModalClose"
            @success="handleFrequencySuccess"
        />

        <SubscriptionSwitchPlanModal
            v-if="showPlanSwitchModal && selectedPlanForSwitch"
            :current-frequency="currentFrequency"
            :current-price="currentPaddleSubscription?.totals?.total || '0'"
            :current-plan-name="
                currentPaddleSubscription?.items?.[0]?.productName
                    ?.replace(/DMARC\/|eye\s*-\s*/g, '')
                    .trim() || t('vue.unknown_plan')
            "
            :next-billing-date="
                currentPaddleSubscription?.next_billing_at?.date || ''
            "
            :new-plan-price-id="selectedPlanForSwitch.priceId"
            :new-plan-name="selectedPlanForSwitch.planName"
            :new-frequency="selectedPlanForSwitch.frequency"
            @close="handlePlanSwitchModalClose"
            @success="handlePlanSwitchSuccess"
        />
    </BillingTabs>

    <TrialEndedPopup />
    <ExtendYourTrialPopup />
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { type IPaddleSubscription } from "@/types/Subscriptions/IPaddleSubscription";
import route from "ziggy-js";
import axios from "axios";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { useTranslations } from "@/composables/useTranslations.ts";

const { t } = useTranslations();

const props = defineProps({
    isTrialing: {
        type: Boolean,
        default: false,
    },
    isSubscribed: {
        type: Boolean,
        default: false,
    },
    trialEndsAt: {
        type: String,
        default: "",
    },
    domainsCount: {
        type: Number,
        default: () => 0,
    },
    paddleClientToken: {
        type: String,
        default: "",
    },
    paddleEnvironment: {
        type: String as () => "production" | "sandbox",
        default: "production",
    },
    paddleSubscription: {
        type: Object as PropType<IPaddleSubscription | null>,
        default: null,
    },
    paddleCustomerPortalUrl: {
        type: String,
        default: "",
    },
    plans: {
        type: Array as () => {
            type: "monthly" | "yearly";
            priceId: string;
            productId: string;
            productName: string;
            productDescription: string;
            totalPrice?: number;
        }[],
        default: () => [],
    },
});

// Reactive data for async loading
const isLoading = ref(true);
const asyncPaddleSubscription = ref<IPaddleSubscription | null>(null);
const asyncPaddleCustomerPortalUrl = ref<string>("");
const asyncPlans = ref<typeof props.plans>([]);

// Use async data if available, fallback to props
const currentPaddleSubscription = computed(
    () => asyncPaddleSubscription.value || props.paddleSubscription,
);
const currentPaddleCustomerPortalUrl = computed(
    () => asyncPaddleCustomerPortalUrl.value || props.paddleCustomerPortalUrl,
);
const currentPlans = computed(() =>
    asyncPlans.value.length > 0 ? asyncPlans.value : props.plans,
);
const payYearly = ref(true);
const inertiaProps = usePage<IRhizomPageProps>();
const hasGodMode = computed(() => inertiaProps.props.hasGodMode);

// Async loading functions
const loadSubscriptionData = async () => {
    try {
        const response = await axios.get(route("billing.subscriptions.data"));
        asyncPaddleSubscription.value = response.data.paddleSubscription;
        asyncPaddleCustomerPortalUrl.value =
            response.data.paddleCustomerPortalUrl;
    } catch (error) {
        console.error("Failed to load subscription data:", error);
    }
};

const loadPlans = async () => {
    try {
        const response = await axios.get(route("billing.subscriptions.plans"));
        asyncPlans.value = response.data.plans;
    } catch (error) {
        console.error("Failed to load plans:", error);
    }
};

const loadAllData = async () => {
    isLoading.value = true;

    // Load data in parallel
    await Promise.all([loadSubscriptionData(), loadPlans()]);

    isLoading.value = false;
};

const selectedPlan = ref(
    undefined as {
        type: "monthly" | "yearly";
        priceId: string;
        productId: string;
        totalPrice?: number;
        productName: string;
        productDescription: string;
    }[],
);

const showFrequencySwitchModal = ref(false);
const showPlanSwitchModal = ref(false);
const selectedPlanForSwitch = ref<{
    priceId: string;
    planName: string;
    frequency: "month" | "year";
} | null>(null);

const handleCloseSubscriptionModal = () => {
    selectedPlan.value = undefined;
    window.location.reload();
};

const handlePlanSelection = (plan: (typeof currentPlans.value)[0][]) => {
    // Prevent selecting Basic plan if user has more than 1 domain
    if (
        isBasicPlanDisabled.value &&
        plan[0].productName.replace(/DMARC\/|eye\s*-\s*/g, "").trim() ===
            "Basic"
    ) {
        return;
    }

    selectedPlan.value = plan;
};

const switchToPlan = (plan: (typeof currentPlans.value)[0][]) => {
    // Prevent switching to Basic plan if user has more than 1 domain
    if (
        isBasicPlanDisabled.value &&
        plan[0].productName.replace(/DMARC\/|eye\s*-\s*/g, "").trim() ===
            "Basic"
    ) {
        return;
    }

    let planToSwitch;
    if (payYearly.value) {
        planToSwitch = plan[1];
    } else {
        planToSwitch = plan[0];
    }

    // Show the plan switch modal instead of directly switching
    selectedPlanForSwitch.value = {
        priceId: planToSwitch.priceId,
        planName: planToSwitch.productName
            .replace(/DMARC\/|eye\s*-\s*/g, "")
            .trim(),
        frequency: planToSwitch.type === "yearly" ? "year" : "month",
    };
    showPlanSwitchModal.value = true;
};

const groupedPlansByProduct = computed(() => {
    // Filter out the plan that user is currently subscribed to
    const filteredPlans = currentPlans.value.filter((plan) => {
        if (!props.isSubscribed || !currentPaddleSubscription.value) {
            return true; // Show all plans if not subscribed
        }

        // Check if this plan matches the current subscription by product name
        const currentProductName =
            currentPaddleSubscription.value.items[0]?.productName;
        return plan.productName !== currentProductName;
    });

    return filteredPlans.reduce(
        (acc, plan) => {
            if (!acc[plan.productId]) {
                acc[plan.productId] = [];
            }
            acc[plan.productId].push(plan);
            return acc;
        },
        {} as Record<string, typeof currentPlans.value>,
    );
});

const isBasicPlanDisabled = computed(() => {
    return props.domainsCount > 1;
});

const getBasicPlanTooltip = computed(() => {
    if (props.domainsCount > 1) {
        return `Basic plan is limited to 1 domain. You currently have ${props.domainsCount} domains. Please upgrade to Scale plan for multiple domains.`;
    }
    return "";
});

const domainsCountToBuy = computed(() => {
    if (
        selectedPlan.value !== undefined &&
        selectedPlan.value[0].productName
            .replace(/DMARC\/|eye\s*-\s*/g, "")
            .trim() === "Scale"
    ) {
        return props.domainsCount < 2 ? 2 : props.domainsCount;
    }

    return props.domainsCount === 0 ? 1 : props.domainsCount;
});

const currentFrequency = computed(() => {
    return (currentPaddleSubscription.value?.period as string) || "month";
});

function visitCustomerPortal() {
    if (currentPaddleCustomerPortalUrl.value) {
        window.open(currentPaddleCustomerPortalUrl.value, "_blank", "noopener");
    } else {
        console.error("Paddle customer portal URL is not set.");
    }
}

function updateFrequency() {
    showFrequencySwitchModal.value = true;
}

const handleFrequencyModalClose = () => {
    showFrequencySwitchModal.value = false;
    // Refresh subscription data after frequency switch
    loadSubscriptionData();
};

const handlePlanSwitchModalClose = () => {
    showPlanSwitchModal.value = false;
    selectedPlanForSwitch.value = null;
    // Refresh subscription data after plan switch
    loadSubscriptionData();
};

const handleFrequencySuccess = () => {
    // Refresh subscription data after successful frequency switch
    loadSubscriptionData();
};

const handlePlanSwitchSuccess = () => {
    // Refresh subscription data after successful plan switch
    loadSubscriptionData();
};

// Load data on component mount
onMounted(() => {
    loadAllData();
});
</script>

<style scoped>
.tooltip-triangle {
    width: 14px;
    height: 14px;
    background: linear-gradient(to bottom right, transparent 55%, #1f2937 45%);
    transform: rotate(45deg);
    position: absolute;
    border-radius: 2px;
    bottom: -6px;
    left: 0;
    right: 0;
    margin: auto;
    z-index: 2;
}
</style>
